<script setup>
// Home dashboard — the signed-in learner's default landing page (route `/home`,
// a ConsumerShell child). Its job is to orient and re-engage in one glance:
// greet, resume the last session, show at-a-glance stats + recent activity, and
// offer a few topics to jump into. It SUMMARISES and links — the full Stats and
// History pages live under /topics. All data comes from existing endpoints:
//   GET /api/me/stats    — lifetime aggregates
//   GET /api/me/sessions — recent sessions (newest first)
//   GET /api/decks       — available topics (+ deck topic names for slugs)
//
// Three blocks (Streak, Weakest topics, Due for review) are intentional
// placeholders — the layout is in place; they'll be wired to real data soon
// (streak needs a new query; weakest topics needs per-deck grouping; due-for-
// review needs the planned user_progress table). See docs/deferred-work.md.
import { ref, onMounted, computed } from "vue";
import { useAuthStore } from "@digitalstreams/saas-core/vue/stores/authStore";

const auth = useAuthStore();

const loading = ref(true);
const error = ref("");
const stats = ref(null);
const sessions = ref([]);
const decks = ref([]);
const topicBySlug = ref({});

const firstName = computed(() => {
  const n = auth.user?.displayName || auth.user?.name || "";
  return n ? n.split(" ")[0] : "there";
});

// New learner = no answered questions yet. Drives the onboarding empty state.
const isNewUser = computed(() => !stats.value || (stats.value.answered || 0) === 0);

const lastSession = computed(() => sessions.value[0] || null);
const recentSessions = computed(() => sessions.value.slice(0, 5));

// A handful of topics to jump into from the dashboard (full list under /topics).
const quickTopics = computed(() => decks.value.slice(0, 4));

// Primary CTA: resume the last deck if we have one with a slug, else "choose a
// topic". Old rows can have a null deck_slug, so guard on it.
const primaryCta = computed(() => {
  const s = lastSession.value;
  if (s && s.deckSlug) {
    return { label: `Continue: ${topic(s.deckSlug)}`, to: playHref(s.deckSlug) };
  }
  return { label: "Choose a topic", to: "/topics" };
});

// Placeholder blocks — laid out now, wired up soon.
const upcoming = [
  {
    key: "streak",
    title: "Day streak",
    icon: "bi-fire",
    color: "#ea580c",
    blurb: "Practise on consecutive days to build and keep a streak."
  },
  {
    key: "weakest",
    title: "Weakest topics",
    icon: "bi-graph-down-arrow",
    color: "#7c3aed",
    blurb: "See which topics need the most work, ranked by accuracy."
  },
  {
    key: "review",
    title: "Due for review",
    icon: "bi-arrow-repeat",
    color: "#0d9488",
    blurb: "Spaced-repetition reminders for questions coming due."
  }
];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric", month: "short", year: "numeric"
  });
}
function topic(slug) {
  if (!slug) return "(unknown deck)";
  return topicBySlug.value[slug] || slug;
}
const playHref = (slug) => `/topics/play/${encodeURIComponent(slug)}`;

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const [stRes, seRes, dRes] = await Promise.all([
      fetch("/api/me/stats", { credentials: "include" }),
      fetch("/api/me/sessions", { credentials: "include" }),
      fetch("/api/decks", { credentials: "include" })
    ]);
    if (stRes.ok) stats.value = (await stRes.json()).stats;
    if (seRes.ok) sessions.value = (await seRes.json()).sessions || [];
    if (dRes.ok) {
      const dData = await dRes.json();
      decks.value = dData.decks || [];
      topicBySlug.value = Object.fromEntries(decks.value.map((d) => [d.slug, d.topic]));
    }
    if (!stRes.ok && !seRes.ok && !dRes.ok) {
      throw new Error("Could not load your dashboard.");
    }
  } catch (e) {
    error.value = e.message || "Could not load your dashboard.";
  } finally {
    loading.value = false;
  }
}
onMounted(load);
</script>

<template>
  <div>
    <!-- Greeting + primary action -->
    <div class="card border-0 shadow-sm mb-3">
      <div class="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h1 class="h4 mb-1">Welcome back, {{ firstName }} <span class="ms-1">👋</span></h1>
          <p class="text-muted mb-0">
            {{ isNewUser
              ? "Let's get you started — pick a topic and answer out loud."
              : "Pick up where you left off, or start a new topic." }}
          </p>
        </div>
        <router-link :to="primaryCta.to" class="btn btn-primary">
          <i class="bi bi-play-fill me-1"></i>{{ primaryCta.label }}
        </router-link>
      </div>
    </div>

    <div v-if="loading" class="text-muted py-3">
      <span class="spinner-border spinner-border-sm me-2"></span>Loading your dashboard…
    </div>
    <div v-else-if="error" class="alert alert-warning py-2">{{ error }}</div>

    <template v-else>
      <!-- New-user onboarding -->
      <div v-if="isNewUser" class="card border-0 shadow-sm mb-3">
        <div class="card-body text-center py-4">
          <i class="bi bi-mic-fill display-6 text-primary opacity-75 d-block mb-2"></i>
          <h2 class="h5">Ready for your first session?</h2>
          <p class="text-muted mb-3">
            Choose a topic and i-Recall will read each question aloud — you answer by speaking.
            It's worth a quick mic check first.
          </p>
          <router-link to="/topics" class="btn btn-primary me-2">
            <i class="bi bi-collection me-1"></i>Browse topics
          </router-link>
          <router-link to="/settings" class="btn btn-outline-secondary">
            <i class="bi bi-mic me-1"></i>Test microphone
          </router-link>
        </div>
      </div>

      <!-- At-a-glance stats -->
      <div v-if="!isNewUser" class="row g-3 mb-3">
        <div class="col-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="text-muted small">Overall accuracy</div>
              <div class="fs-3 fw-semibold">{{ stats.accuracy }}%</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="text-muted small">First-try accuracy</div>
              <div class="fs-3 fw-semibold">{{ stats.firstTryAccuracy }}%</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="text-muted small">Questions answered</div>
              <div class="fs-3 fw-semibold">{{ stats.answered }}</div>
            </div>
          </div>
        </div>
        <div class="col-6 col-lg-3">
          <div class="card border-0 shadow-sm h-100">
            <div class="card-body">
              <div class="text-muted small">Sessions</div>
              <div class="fs-3 fw-semibold">{{ stats.sessions }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <!-- Left column: recent activity + jump back in -->
        <div class="col-12 col-lg-7">
          <div v-if="!isNewUser" class="card border-0 shadow-sm mb-3">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h2 class="h6 mb-0"><i class="bi bi-clock-history me-2"></i>Recent activity</h2>
                <router-link to="/topics/history" class="small">Full history →</router-link>
              </div>
              <p v-if="recentSessions.length === 0" class="text-muted small mb-0">
                No sessions yet.
              </p>
              <table v-else class="table table-sm align-middle mb-0">
                <tbody>
                  <tr v-for="s in recentSessions" :key="s.sessionId">
                    <td class="text-truncate" :title="topic(s.deckSlug)">{{ topic(s.deckSlug) }}</td>
                    <td class="text-muted small text-nowrap">{{ fmtDate(s.startedAt) }}</td>
                    <td class="text-end">
                      <span
                        v-if="s.accuracy !== null"
                        class="badge"
                        :class="s.accuracy >= 70 ? 'bg-success' : s.accuracy >= 40 ? 'bg-warning text-dark' : 'bg-danger'"
                      >{{ s.accuracy }}%</span>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h2 class="h6 mb-0"><i class="bi bi-collection me-2"></i>Jump back in</h2>
                <router-link to="/topics" class="small">All topics →</router-link>
              </div>
              <p v-if="quickTopics.length === 0" class="text-muted small mb-0">
                No topics available yet.
              </p>
              <div v-else class="row g-2">
                <div v-for="d in quickTopics" :key="d.slug" class="col-12 col-sm-6">
                  <router-link
                    :to="playHref(d.slug)"
                    class="d-flex align-items-center border rounded p-2 text-decoration-none text-body h-100"
                  >
                    <i class="bi bi-play-circle fs-5 text-primary me-2"></i>
                    <span class="flex-grow-1 text-truncate" :title="d.topic">{{ d.topic }}</span>
                    <span class="text-muted small ms-2 text-nowrap">{{ d.itemCount }} Q</span>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column: coming-soon widgets (laid out now, wired up soon) -->
        <div class="col-12 col-lg-5">
          <div v-for="u in upcoming" :key="u.key" class="card shadow-sm mb-3 rm-soon">
            <div class="card-body">
              <div class="d-flex align-items-center mb-1">
                <span class="rm-soon-icon" :style="{ background: u.color + '22', color: u.color }">
                  <i class="bi" :class="u.icon"></i>
                </span>
                <h2 class="h6 mb-0 ms-2">{{ u.title }}</h2>
                <span class="badge rounded-pill bg-light text-muted border ms-auto">Coming soon</span>
              </div>
              <p class="text-muted small mb-0">{{ u.blurb }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.rm-soon {
  border: 1px dashed var(--bs-border-color);
}
.rm-soon-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px; height: 30px;
  border-radius: 8px;
  font-size: 0.95rem;
}
</style>
